import { useCodatAuth } from "@codat/client-cookie-manager";
import { Col, Pagination, Row } from "@codat/orchard-ui";
import { useEffect, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";

import { IRepoProps } from "../../interfaces/Props";
import { CompanySummary, ConnectorStatusSummary } from "../../Models";
import { isOrderAsc } from "../../utils";
import { CompanyStatusType } from "../../utilsCompanyStatus";
import { ConnectionStatusType } from "../../utilsConnectorStatus";
import { opsSettings, SortableColumnKeys } from "../../App";
import CompaniesTable from "../CompaniesTable/CompaniesTable";
import { SearchBox } from "../SearchBox/SearchBox";

export interface ICompaniesViewProps extends IRepoProps {
  isLatest(integration: string, version: string): boolean;
  syncStatusFilter: string;
  connectorStatusFilter: string;
  setSyncStatusFilter: (status: CompanyStatusType) => void;
  setConnectorStatusFilter: (status: ConnectionStatusType) => void;
  connectors: ConnectorStatusSummary[];
  ops: opsSettings;
  setOps: React.Dispatch<React.SetStateAction<opsSettings>>;
}

const CompaniesView: React.FC<ICompaniesViewProps> = ({
  repo,
  isLatest,
  syncStatusFilter,
  setSyncStatusFilter,
  setConnectorStatusFilter,
  connectorStatusFilter,
  ops,
  setOps,
}) => {
  const [listItems, setListItems] = useState<CompanySummary[]>([]);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [perPageNumber, setPerPageNumber] = useState(10);

  const sortHandler = (column: SortableColumnKeys) => {
    if (ops.sortBy !== column) {
      setOps({ ...ops, sort: "asc", sortBy: column });
    } else {
      setOps({ ...ops, sort: isOrderAsc(ops.sort) ? "desc" : "asc" });
    }
  };

  const { userData } = useCodatAuth();

  const { promiseInProgress } = usePromiseTracker();

  useEffect(() => {
    const token = userData?.access_token;

    if (token) {
      trackPromise(
        repo.GetCompaniesAsync(
          ops.page,
          perPageNumber,
          ops.sort,
          ops.sortBy,
          ops.search,
          token,
          syncStatusFilter,
          connectorStatusFilter
        )
      ).then((res) => {
        setListItems(res.results);
        setTotalResults(res.totalResults);
      });
    }
  }, [
    ops.page,
    ops.search,
    ops.sort,
    ops.sortBy,
    syncStatusFilter,
    connectorStatusFilter,
    perPageNumber,
    repo,
    userData?.access_token,
  ]);

  return (
    <>
      <Row>
        <Col>
          <SearchBox
            setSearch={setOps}
            setConnectorStatusFilter={setConnectorStatusFilter}
            setSyncStatusFilter={setSyncStatusFilter}
            isLoading={promiseInProgress}
          />
        </Col>
      </Row>
      <CompaniesTable
        listItems={listItems}
        repo={repo}
        token={userData?.access_token}
        onSort={sortHandler}
        sortDirection={ops.sort}
        sortBy={ops.sortBy}
        isLatest={isLatest}
        isLoading={promiseInProgress}
        numberOfSkeletonRows={perPageNumber}
      />
      <Pagination
        label="Pagination"
        pageIndex={ops.page}
        perPageOptions={[10, 20, 50]}
        onUpdatePerPage={(numberOfItems) => setPerPageNumber(numberOfItems)}
        onUpdatePageIndex={(val) => setOps({ ...ops, page: val })}
        perPage={perPageNumber}
        total={totalResults}
        isSkeleton={promiseInProgress}
      />
    </>
  );
};

export default CompaniesView;
